import { CustomError, CustomErrorType } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import getAnalytics from 'Modules/analytics';
import { queryParamsFromObject } from 'Modules/converter/index';
import { getNumberProperty, getStringProperty } from 'Utils/typeGuards';
import { getSearchId } from 'Utils/userStorage';

import type { ServerModel } from '../types';
import { getInstanceId } from './statusPayload';

const { backupServerUrl, serverUrl, timeout } = requestConfig;

const analytics = getAnalytics();

// The intersection narrows `signal` from `AbortSignal | null | undefined`
// (DOM lib `RequestInit.signal`) to `AbortSignal | undefined`.
// Downstream code calls `withAbortSignal(config.signal)` which only accepts `AbortSignal | undefined`.
type RequestConfig = RequestInit & { signal?: AbortSignal };

export function abortControllerCreator(): () => AbortSignal {
  const abortControllerRef = { current: new AbortController() };
  return () => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };
}

export async function GET<T = unknown>(
  endpoint: string,
  queryParams?: RequestExtra,
  config?: RequestConfig,
): Promise<T> {
  return request<T>(serverUrl, endpoint, queryParams, { ...config, method: 'GET' });
}

export async function GETBACKUP<T = unknown>(
  endpoint: string,
  queryParams?: RequestExtra,
  config?: RequestConfig,
): Promise<T> {
  return request<T>(backupServerUrl, endpoint, queryParams, { ...config, method: 'GET' });
}

export async function POST<T = unknown>(
  endpoint: string,
  queryParams?: RequestExtra,
  data?: BodyInit,
  config?: RequestConfig,
): Promise<T> {
  return request<T>(serverUrl, endpoint, queryParams, {
    ...config,
    method: 'POST',
    body: data,
  });
}

export async function POSTBACKUP<T = unknown>(
  endpoint: string,
  queryParams?: RequestExtra,
  data?: BodyInit,
  config?: RequestConfig,
): Promise<T> {
  return request<T>(backupServerUrl, endpoint, queryParams, {
    ...config,
    method: 'POST',
    body: data,
  });
}

export function sendBeacon(endpoint: string, data?: BodyInit | null): boolean {
  return navigator.sendBeacon(`${serverUrl}/${endpoint}`, data);
}

/**
 * The one spot where the Client turns raw response text into a structured
 * value. Returns `unknown` so callers can choose to cast (`request<T>`) or
 * narrow with a predicate; throws a `CustomError` with `url` / `details`
 * attached when the body is not valid JSON.
 */
function parseJsonOrThrow(text: string, url: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    throw new CustomError(`request for '${url}' returned a non-JSON response body.`, {
      url,
      details: text,
    });
  }
}

// request is the typed boundary for server JSON: T is trusted at caller sites
// because the underlying fetch / JSON.parse result is fundamentally `unknown`.
async function request<T>(
  serverUrl: string,
  endpoint: string,
  queryParams: RequestExtra | undefined,
  config: RequestConfig & { method: string },
): Promise<T> {
  const url = `${serverUrl}/${endpoint}${queryParams ? `?${queryParamsFromObject(queryParams)}` : ''}`;

  let timeoutExceeded = false;
  const abortController = handleAbortController(config.signal);
  const timeoutController = setTimeout(() => {
    timeoutExceeded = true;
    abortController.abort();
  }, timeout);

  let requestError: CustomError | null = null;

  const toRequestError = (err: Error): CustomError =>
    timeoutExceeded
      ? new CustomError(`request timeout exceeded for '${url}'.`, { error: err, url })
      : new CustomError(`request for '${url}' failed with error: '${err.message}'.`, { error: err, url });

  const headers = new Headers(config?.headers);

  if (analytics.isAnalyticsEnabled) {
    const searchId = getSearchId();

    if (searchId) {
      headers.set('Content-Language', `${analytics.userId};${analytics.sessionId};${searchId}`);
    } else {
      headers.set('Content-Language', `${analytics.userId};${analytics.sessionId}`);
    }
  }

  const response = await window
    .fetch(url, {
      ...config,
      headers,
      signal: abortController.signal,
    })
    .catch((err) => {
      requestError = toRequestError(err);
    });

  if (requestError) {
    throw requestError;
  }

  if (response?.ok) {
    clearTimeout(timeoutController);

    // 204 No Content is a valid success with an intentionally empty body (e.g. garage save).
    if (response.status === 204) {
      return undefined as T;
    }

    let responseText: string;
    try {
      responseText = await response.text();
    } catch (err) {
      throw toRequestError(err as Error);
    }

    if (responseText) {
      return parseJsonOrThrow(responseText, url) as T;
    }

    throw new CustomError(`request for '${url}' returned an empty response body.`, { url });
  }

  if (response) {
    throw new CustomError(`request for '${url}' failed with status: '${response.status}'.`, {
      status: response.status,
      url,
    });
  }

  throw new CustomError(`request for '${url}' returned no response and no error.`, { url });
}

const createServerInstanceAbortController = abortControllerCreator();
const statusServerUrl = requestConfig.useDevServer
  ? 'https://localhost.convermax.dev'
  : 'https://client.convermax.com';

async function getServerInstance(): Promise<string> {
  const abortController = handleAbortController(createServerInstanceAbortController());
  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, timeout);

  const response = await window
    .fetch(`${statusServerUrl}/status.json`, { signal: abortController.signal })
    .catch(() => null);

  clearTimeout(timeoutId);

  if (!response) {
    return 'unknown';
  }

  try {
    const payload: unknown = await response.json();
    return getInstanceId(payload) ?? 'unknown';
  } catch (error) {
    console.error(error);
  }

  return 'unknown';
}

type RequestAbortController = Pick<AbortController, 'abort' | 'signal'>;

function handleAbortController(parentSignal?: AbortSignal): RequestAbortController {
  const abortController = new AbortController();

  if (parentSignal) {
    followSignal(abortController, parentSignal);
  }

  return {
    abort: (reason?: unknown) => abortController.abort(reason),
    signal: abortController.signal,
  };
}

// https://github.com/whatwg/dom/issues/920#issuecomment-726722960
// Keep this shim until all supported browsers provide AbortSignal.any().
function followSignal(followingAbortController: AbortController, parentSignal: AbortSignal): void {
  if (followingAbortController.signal.aborted) {
    return;
  }

  if (parentSignal.aborted) {
    followingAbortController.abort(parentSignal.reason);
    return;
  }

  const onAbort = () => {
    followingAbortController.abort(parentSignal.reason);
  };
  parentSignal.addEventListener('abort', onAbort, { once: true });

  followingAbortController.signal.addEventListener(
    'abort',
    () => {
      parentSignal.removeEventListener('abort', onAbort);
    },
    { once: true },
  );
}

const emptySearchResponse = {
  Actions: [],
  Corrections: [],
  Facets: [],
  Items: [],
  Messages: [],
  OriginalQuery: '',
  Query: '',
  TotalHits: 0,
  Extra: {},
  _clientError: null,
} satisfies Omit<ServerModel.SearchResponse, 'State'>;

export function asCmError(e: unknown): {
  type?: string;
  message?: string;
  status?: number;
  requestUrl?: string;
} {
  return {
    type: getStringProperty(e, 'type'),
    message: getStringProperty(e, 'message'),
    status: getNumberProperty(e, 'status'),
    requestUrl: getStringProperty(e, 'requestUrl'),
  };
}

export async function getErrorFacetsResponse(err?: unknown): Promise<ServerModel.FacetsResponse> {
  const { type } = asCmError(err);

  if (type === CustomErrorType.requestTimeout || type === CustomErrorType.requestAborted) {
    return { Facets: [] };
  }

  return { Facets: [] };
}

export async function getErrorSearchResponse(err?: unknown): Promise<ServerModel.SearchResponse> {
  const { type, status } = asCmError(err);

  if (type === CustomErrorType.requestTimeout) {
    return { ...emptySearchResponse, State: 'timeout' };
  }

  if (type === CustomErrorType.requestAborted) {
    return { ...emptySearchResponse, State: 'cancelled' };
  }

  const instance = await getServerInstance();
  const _clientError = { status, ...analytics, timestamp: new Date().toUTCString(), instance };

  if (type === CustomErrorType.failedToFetch) {
    return {
      ...emptySearchResponse,
      State: 'networkError',
      _clientError,
    };
  }

  if (status !== undefined && status >= 400 && status < 500) {
    return { ...emptySearchResponse, State: 'badRequest', _clientError };
  }

  if (status === undefined || Number.isNaN(status)) {
    console.error('search request failed with unknown error', err);
    return { ...emptySearchResponse, State: 'unknown', _clientError };
  }

  return { ...emptySearchResponse, State: 'unknown', _clientError };
}
